import {t} from "./i18n/index.js";

// Shared, anchor-based info tooltip for Digitalisering's plain DOM UI (the
// settings panel, and any future tool panel) - one small "ⓘ" icon per
// setting, hover/focus reveals a shared tooltip bubble positioned next to
// it and clamped to the viewport so it never gets clipped by the sidebar's
// own edge.
//
// Distinct from HoverTooltip.js: that one is mouse-position-based, for
// WebGL/canvas objects (a segment's insert-vertex handle, the copy-
// coordinates icon) that have no real DOM element of their own to anchor
// to. This one anchors to a real DOM element via getBoundingClientRect(),
// which is both simpler and more precisely positioned for ordinary sidebar
// controls.
//
// Plain DOM (no jQuery dependency) - callers using jQuery can still
// `$(container).append(createInfoIcon(text))` directly, since jQuery's
// .append() accepts raw DOM nodes.

let bubbleEl = null;

function ensureBubble(){
	if(bubbleEl) return bubbleEl;
	bubbleEl = document.createElement("div");
	bubbleEl.className = "digitization-info-tooltip";
	document.body.appendChild(bubbleEl);
	return bubbleEl;
}

function showBubble(anchorEl, text){
	const bubble = ensureBubble();
	bubble.textContent = text;
	bubble.style.display = "block";
	// measure before positioning off-screen defaults so offsetWidth/Height
	// below reflect the actual (just-set) text content
	bubble.style.left = "-9999px";
	bubble.style.top = "-9999px";

	const anchorRect = anchorEl.getBoundingClientRect();
	const bubbleRect = bubble.getBoundingClientRect();
	const margin = 8;

	let left = anchorRect.left;
	const maxLeft = window.innerWidth - bubbleRect.width - margin;
	left = Math.min(left, Math.max(margin, maxLeft));
	left = Math.max(left, margin);

	let top = anchorRect.bottom + 6;
	const maxTop = window.innerHeight - bubbleRect.height - margin;
	if(top > maxTop){
		// no room below - flip above the icon instead
		top = anchorRect.top - bubbleRect.height - 6;
	}
	top = Math.max(top, margin);

	bubble.style.left = left + "px";
	bubble.style.top = top + "px";
}

function hideBubble(){
	if(bubbleEl) bubbleEl.style.display = "none";
}

// Creates a small "ⓘ" icon that shows `text` in the shared tooltip bubble
// on mouse hover or keyboard focus. Returns the icon element - append it
// next to whatever control it explains.
export function createInfoIcon(text){
	const icon = document.createElement("span");
	icon.className = "digitization-info-icon";
	icon.textContent = "ⓘ";
	icon.setAttribute("tabindex", "0");
	icon.setAttribute("role", "img");
	icon.setAttribute("aria-label", t("Information: ") + text);

	icon.addEventListener("mouseenter", () => showBubble(icon, text));
	icon.addEventListener("mouseleave", hideBubble);
	icon.addEventListener("focus", () => showBubble(icon, text));
	icon.addEventListener("blur", hideBubble);

	return icon;
}
